import {
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_DESCRIPTION,
  QUESTION_RATING,
  QUESTION_NPS,
  QUESTION_DRILLDOWN,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_CONSTANT_SUM,
  QUESTION_VAN_WESTENDORP,
} from 'data/nodeTypes';
import {
  ANSWER_OTHER,
  ANSWER_NONE_OF_THE_ABOVE,
} from 'containers/QuestionnairePage/constants';

const AIActionCommonClassNames =
  'background-white content-fit text-size-12 line-height-16 align-middle';

export const QUESTION_TYPES_WITH_NO_GENERATE_ANSWERS = [
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_DESCRIPTION,
  QUESTION_RATING,
  QUESTION_NPS,
  QUESTION_DRILLDOWN,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_CONSTANT_SUM,
  QUESTION_VAN_WESTENDORP,
];

export const getAiActionsExcludeList = (type, questionType) => {
  if (type === 'answer') {
    return ['generate-answers'];
  }

  if (
    questionType &&
    QUESTION_TYPES_WITH_NO_GENERATE_ANSWERS.includes(questionType)
  ) {
    return ['generate-answers'];
  }

  return [];
};

// Other and None of the above are not numbered in the questionnaire the AI
// builder receives (they are sent apart from the answers list), so they are
// referenced by name.
const SPECIAL_ANSWER_REFERENCES = {
  [ANSWER_OTHER]: "the 'Other' answer",
  [ANSWER_NONE_OF_THE_ABOVE]: "the 'None of the above' answer",
};

export const getAnswerReference = (Ax, answerType) =>
  SPECIAL_ANSWER_REFERENCES[answerType] ?? `answer ${Ax}`;

const DropdownAIActions = [
  {
    id: 'generate-answers',
    text: 'Generate answers',
    className: `${AIActionCommonClassNames} margin-btm-xxs-important margin-top-none-important`,
    iconType: 'generate-answers-color',
    aiMessageType: {
      question: ({ Qx, isScreening = false }) =>
        `Consider the question prompt in ${
          isScreening ? 'S' : ''
        }Q${Qx} and the given answers, if any, and generate more answers in ${
          isScreening ? 'S' : ''
        }Q${Qx}`,
    },
    types: ['question'],
  },
  {
    id: 'rephrase',
    text: 'Rephrase',
    className: `${AIActionCommonClassNames} margin-btm-xxs-important margin-top-none-important`,
    iconType: 'rephrase-color',
    aiMessageType: {
      question: ({ Qx, option = 'Rephrase', isScreening = false }) =>
        `${option} question prompt in ${isScreening ? 'S' : ''}Q${Qx}`,
      answer: ({ Qx, answerRef, option = 'Rephrase', isScreening = false }) =>
        `${option} ${answerRef} in ${isScreening ? 'S' : ''}Q${Qx}`,
    },
    types: ['question', 'answer'],
    options: ['Broaden', 'Narrow'],
  },
  {
    id: 'set-tone',
    text: 'Set tone',
    className: `${AIActionCommonClassNames} margin-btm-xxs-important`,
    iconType: 'set-tone-color',
    aiMessageType: {
      question: ({ Qx, option = '', isScreening = false }) =>
        `Change the tone of voice at the question prompt in ${
          isScreening ? 'S' : ''
        }Q${Qx} to ${option}`,
      answer: ({ Qx, answerRef, option = '', isScreening = false }) =>
        `Change the tone of voice at ${answerRef} in ${
          isScreening ? 'S' : ''
        }Q${Qx} to ${option}`,
    },
    types: ['question', 'answer'],
    options: [
      'Professional',
      'Casual',
      'Enthusiastic',
      'Informative',
      'Funny',
      'Formal',
      'Friendly',
      'Conversational',
      '...',
    ],
  },
  {
    id: 'translate',
    text: 'Translate',
    className: `${AIActionCommonClassNames} margin-btm-none-important`,
    iconType: 'translate-color',
    aiMessageType: {
      question: ({ Qx, option = '', isScreening = false }) =>
        `Translate question prompt in ${
          isScreening ? 'S' : ''
        }Q${Qx} to ${option}`,
      answer: ({ Qx, answerRef, option = '', isScreening = false }) =>
        `Translate ${answerRef} in ${
          isScreening ? 'S' : ''
        }Q${Qx} to ${option}`,
    },
    types: ['question', 'answer'],
    options: [
      'English',
      'Spanish',
      'Arabic',
      'Chinese',
      'French',
      'German',
      'Portuguese',
      '...',
    ],
  },
];

export const getAvailableDropdownAIActions = (
  type,
  Qx,
  Ax,
  excluded = [],
  isScreening = false,
  answerType = null,
) => {
  const answerRef = getAnswerReference(Ax, answerType);

  return DropdownAIActions.filter(
    (action) => !excluded.includes(action.id),
  ).map((action) => ({
    ...action,
    aiMessageTypeWithoutOption:
      type === 'question'
        ? action.aiMessageType[type]({
            Qx,
            option: '{{option}}',
            isScreening,
          })
        : action.aiMessageType[type]({
            Qx,
            answerRef,
            option: '{{option}}',
            isScreening,
          }),
    aiMessageType:
      type === 'question'
        ? action.aiMessageType[type]({ Qx, isScreening })
        : action.aiMessageType[type]({ Qx, answerRef, isScreening }),
  }));
};
